import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, HelpCircle, FileText, CreditCard, Users, Star, Settings, Mail, ChevronDown, ThumbsUp, ThumbsDown, CheckCircle } from 'lucide-react';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { useLanguage } from '../i18n/LanguageContext';
import { FAQ_CATEGORIES, FAQ_CATEGORY_LABELS, FAQ_DATA, FAQ_UI, getQuestionCount, type FAQCategoryId } from '../data/faqData';
import { AdSlot } from '../components/ads/AdSlot';
interface FAQPageProps {
  onNavigate: (page: string) => void;
}
const CATEGORY_ICONS: Record<FAQCategoryId, React.ReactNode> = {
  general: <HelpCircle className="w-4 h-4" data-id="element-22358" />,
  reports: <FileText className="w-4 h-4" data-id="element-22359" />,
  payment: <CreditCard className="w-4 h-4" data-id="element-22360" />,
  consultations: <Users className="w-4 h-4" data-id="element-22361" />,
  astrologers: <Star className="w-4 h-4" data-id="element-22362" />,
  tech: <Settings className="w-4 h-4" data-id="element-22363" />
};
// Individual FAQ item with helpful feedback
function FAQAccordionItem({
  item,
  isOpen,
  onToggle,
  ui
}: {
  item: {
    id: string;
    question: string;
    answer: string;
  };
  isOpen: boolean;
  onToggle: () => void;
  ui: Record<string, string>;
}) {
  const [feedback, setFeedback] = useState<'yes' | 'no' | null>(null);
  return <div className="border border-slate-200 rounded-2xl bg-white overflow-hidden transition-shadow duration-200 hover:shadow-sm" data-id="element-22364">
      <button onClick={onToggle} className="w-full flex justify-between items-center p-5 sm:p-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset" aria-expanded={isOpen} data-id="element-22365">
        <span className="font-semibold text-slate-900 pr-4 leading-snug" data-id="element-22366">
          {item.question}
        </span>
        <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-22367" />
      </button>
      <AnimatePresence initial={false} data-id="element-22368">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1]
      }} className="overflow-hidden" data-id="element-22369">
            <div className="px-5 sm:px-6 pb-5 sm:pb-6" data-id="element-22370">
              <div className="text-slate-600 text-sm leading-relaxed mb-5 whitespace-pre-line" data-id="element-22371">
                {item.answer}
              </div>

              {/* Helpful feedback */}
              <div className="flex items-center gap-3 pt-4 border-t border-slate-100" data-id="element-22372">
                {feedback === null ? <>
                    <span className="text-xs text-slate-400 font-medium" data-id="element-22373">
                      {ui.helpful}
                    </span>
                    <button onClick={e => {
                e.stopPropagation();
                setFeedback('yes');
              }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 bg-slate-50 hover:bg-emerald-50 hover:text-emerald-600 transition-colors" data-id="element-22374">
                      <ThumbsUp className="w-3.5 h-3.5" data-id="element-22375" /> {ui.yes}
                    </button>
                    <button onClick={e => {
                e.stopPropagation();
                setFeedback('no');
              }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 bg-slate-50 hover:bg-red-50 hover:text-red-500 transition-colors" data-id="element-22376">
                      <ThumbsDown className="w-3.5 h-3.5" data-id="element-22377" /> {ui.no}
                    </button>
                  </> : <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium" data-id="element-22378">
                    <CheckCircle className="w-3.5 h-3.5" data-id="element-22379" /> {ui.thankYou}
                  </span>}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function FAQPage({
  onNavigate
}: FAQPageProps) {
  const {
    language
  } = useLanguage();
  const ui = FAQ_UI[language];
  const categoryLabels = FAQ_CATEGORY_LABELS[language];
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<FAQCategoryId>('general');
  const [openId, setOpenId] = useState<string | null>(null);
  const filteredFAQs = useMemo(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return FAQ_DATA.filter(item => item.question.toLowerCase().includes(query) || item.answer.toLowerCase().includes(query));
    }
    return FAQ_DATA.filter(item => item.categoryId === activeCategory);
  }, [searchQuery, activeCategory]);
  // Reset open accordion when category or search changes
  const handleCategoryChange = (id: FAQCategoryId) => {
    setActiveCategory(id);
    setOpenId(null);
  };
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setOpenId(null);
  };
  return <div className="min-h-screen bg-slate-50 pb-24" data-id="element-22380">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 pt-6 pb-12" data-id="element-22381">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22382">
          <Breadcrumbs items={[{
          label: ui.breadcrumbHome,
          onClick: () => onNavigate('home')
        }, {
          label: ui.breadcrumbFaq
        }]} className="mb-8" data-id="element-22383" />

          <div className="text-center max-w-2xl mx-auto" data-id="element-22384">
            <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm" data-id="element-22385">
              <HelpCircle className="w-8 h-8" data-id="element-22386" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" data-id="element-22387">
              {ui.title}
            </h1>
            <p className="text-lg text-slate-600 mb-8" data-id="element-22388">{ui.subtitle}</p>

            <div className="relative max-w-xl mx-auto" data-id="element-22389">
              <SearchBar placeholder={ui.searchPlaceholder} value={searchQuery} onChange={handleSearchChange} className="shadow-md" data-id="element-22390" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-22391">
        <div className="flex flex-col md:flex-row gap-8 items-start" data-id="element-22392">
          {/* Categories Sidebar */}
          {!searchQuery && <div className="w-full md:w-64 flex-shrink-0 space-y-1" data-id="element-22393">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 px-3" data-id="element-22394">
                {ui.categories}
              </h3>
              {FAQ_CATEGORIES.map(category => {
            const count = getQuestionCount(category.id);
            return <button key={category.id} onClick={() => handleCategoryChange(category.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${activeCategory === category.id ? 'bg-indigo-50 text-indigo-700 shadow-sm' : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'}`} data-id="element-22395">
                    <span className={activeCategory === category.id ? 'text-indigo-500' : 'text-slate-400'} data-id="element-22396">
                      {CATEGORY_ICONS[category.id]}
                    </span>
                    <span className="flex-1 text-left" data-id="element-22397">
                      {categoryLabels[category.id]}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${activeCategory === category.id ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-22398">
                      {count}
                    </span>
                  </button>;
          })}
            </div>}

          {/* FAQ List */}
          <div className="flex-1 w-full min-w-0" data-id="element-22399">
            {searchQuery && <div className="mb-6 flex items-center justify-between" data-id="element-22400">
                <h2 className="text-lg font-bold text-slate-900" data-id="element-22401">
                  {ui.searchResults}
                </h2>
                <span className="text-sm text-slate-500" data-id="element-22402">
                  {ui.found}: {filteredFAQs.length}
                </span>
              </div>}

            {!searchQuery && <div className="mb-6 hidden md:flex items-center justify-between" data-id="element-22403">
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-22404">
                  {categoryLabels[activeCategory]}
                </h2>
                <span className="text-sm text-slate-400" data-id="element-22405">
                  {getQuestionCount(activeCategory)} {ui.questionsCount}
                </span>
              </div>}

            {filteredFAQs.length > 0 ? <motion.div key={searchQuery ? 'search' : activeCategory} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-4" data-id="element-22406">
                {filteredFAQs.map(item => <FAQAccordionItem key={item.id} item={item} isOpen={openId === item.id} onToggle={() => setOpenId(openId === item.id ? null : item.id)} ui={ui} data-id="element-22407" />)}
              </motion.div> : <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 border-dashed" data-id="element-22408">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" data-id="element-22409" />
                <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-22410">
                  {ui.notFound}
                </h3>
                <p className="text-slate-500 max-w-sm mx-auto" data-id="element-22411">
                  {ui.notFoundHint}
                </p>
                <Button variant="outline" className="mt-6" onClick={() => setSearchQuery('')} data-id="element-22412">
                  {ui.resetSearch}
                </Button>
              </div>}
          </div>
        </div>

        {/* Ad Banner */}
        <div className="mt-12" data-id="element-22413">
          <AdSlot slotId="SLOT-14" onNavigate={onNavigate} data-id="element-22414" />
        </div>

        {/* Support CTA */}
        <div className="mt-16 bg-gradient-to-br from-slate-900 to-indigo-950 rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden shadow-xl" data-id="element-22415">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-22416" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-22417" />

          <div className="relative z-10" data-id="element-22418">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-sm border border-white/20" data-id="element-22419">
              <Mail className="w-8 h-8 text-white" data-id="element-22420" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" data-id="element-22421">
              {ui.ctaTitle}
            </h2>
            <p className="text-indigo-200 mb-8 max-w-xl mx-auto text-lg" data-id="element-22422">
              {ui.ctaSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-22423">
              <button onClick={() => onNavigate('contact')} className="inline-flex items-center justify-center rounded-xl font-medium text-base px-6 py-3 bg-white text-slate-900 hover:bg-slate-100 transition-colors shadow-lg" data-id="element-22424">
                {ui.ctaEmail}
              </button>
              <button onClick={() => onNavigate('astrologers')} className="inline-flex items-center justify-center rounded-xl font-medium text-base px-6 py-3 border border-white/30 text-white hover:bg-white/10 transition-colors" data-id="element-22425">
                {ui.ctaAstrologer}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}